import React, { useMemo } from 'react';
import { Card, Col, Row, Badge } from 'react-bootstrap';
import ApexCharts from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { useAppContext } from 'providers/AppProvider';
import Flex from 'components/common/Flex';

const CommunicationTracker = () => {
    const { generalDashboardData } = useSelector((state) => state.dashboard);
    const { config: { isDark } } = useAppContext();

    const communication = generalDashboardData?.communication || {
        tot_received: 0,
        tot_notresponded: 0,
        avg_respmins: 0
    };

    const tot_received = Number(communication.tot_received || 0);
    const tot_notresponded = Number(communication.tot_notresponded || 0);
    const responded = Math.max(0, tot_received - tot_notresponded);
    const avg_respmins = communication.avg_respmins || 0;

    const responseRate = tot_received > 0 ? Math.round((responded / tot_received) * 100) : 0;

    let chartSeries = [responded, tot_notresponded];
    if (tot_received === 0) {
        chartSeries = [];
    }

    const chartOptions = useMemo(() => ({
        chart: {
            type: 'donut',
            fontFamily: 'Inter, sans-serif',
        },
        labels: ['Responded', 'Not responded'],
        colors: ['#10b981', '#f59e0b'],
        stroke: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
            pie: {
                donut: {
                    size: '72%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '12px',
                            color: isDark ? '#94a3b8' : '#64748b',
                            offsetY: 20
                        },
                        value: {
                            show: true,
                            fontSize: '14px',
                            color: isDark ? '#f8fafc' : '#1e293b',
                            offsetY: -15,
                            formatter: () => `${responseRate}%`
                        },
                        total: {
                            show: true,
                            label: 'Resolved',
                            fontSize: '16px',
                            color: isDark ? '#f8fafc' : '#1e293b',
                            formatter: () => `${responseRate}%`
                        }
                    }
                }
            }
        },
        noData: {
            text: 'No Communications',
            align: 'center',
            verticalAlign: 'middle',
            offsetY: -10,
            style: {
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: '14px',
                fontFamily: 'inherit'
            }
        },
        legend: { show: false },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                if (typeof seriesIndex !== 'number' || seriesIndex < 0 || seriesIndex >= series.length) {
                    return '';
                }

                const label = w.globals.labels[seriesIndex];
                const value = series[seriesIndex];
                const color = w.globals.colors[seriesIndex];

                // Calculate total from series
                const total = series.reduce((acc, val) => acc + val, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

                const bgColor = isDark ? '#0f172a' : '#ffffff';
                const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0';
                const titleColor = isDark ? '#94a3b8' : '#64748b';
                const textColor = isDark ? '#f8fafc' : '#1e293b';

                return `
                    <div class="px-3 py-2 rounded-3 shadow-lg" style="background-color: ${bgColor}; border: 1px solid ${borderColor}; min-width: 180px;">
                        <div class="fw-semibold fs-11 mb-2 pb-2 border-bottom" style="color: ${titleColor}; border-color: ${borderColor} !important;">
                            Communication Status
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; flex-shrink: 0;"></div>
                            <span class="fw-bold fs-11 text-uppercase" style="color: ${textColor};">${label}:</span>
                            <span class="fw-bold fs-11 ms-auto" style="color: ${color};">${percentage}%</span>
                        </div>
                    </div>
                `;
            }
        },
        states: {
            hover: { filter: { type: 'darken', value: 0.9 } }
        }
    }), [responseRate, tot_received, isDark]);

    return (
        <Col xs={12} md={6} className="mb-4">
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden" style={{ height: '400px', fontFamily: "'Inter', sans-serif" }}>
                <Card.Body className="p-4">
                    <Flex justifyContent="between" alignItems="start" className="mb-4">
                        <div>
                            <h5 className="mb-1 fw-bold fs-9 text-900 d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon="comment" className="text-info fs-8" />
                                Payer Communication Efficiency                            </h5>
                            <p className="mb-0 text-500 fs--1 fw-semi-bold">
                                Monitoring responsiveness to insurance queries and pended tasks
                            </p>
                        </div>
                        <div className="badge badge-subtle-primary rounded-pill px-2 py-1 fs--2 fw-bold text-uppercase">
                            Live Status
                        </div>
                    </Flex>

                    <Row className="align-items-center g-0">
                        <Col xs={12} sm={6} className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                            <ApexCharts
                                options={chartOptions}
                                series={chartSeries}
                                type="donut"
                                width="100%"
                                height="100%"
                            />
                        </Col>

                        <Col xs={12} sm={6}>
                            <div className="d-flex flex-column gap-3 ps-sm-3 mt-3 mt-sm-0">
                                <div className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: isDark ? 'rgba(67, 56, 202, 0.2)' : '#e0e7ff', color: isDark ? '#818cf8' : '#4338ca' }}>
                                            <FontAwesomeIcon icon="envelope" className="fs--1" />
                                        </div>
                                        <span className={`fs-9 fw-bold ${isDark ? 'text-400' : 'text-600'}`}>Total Received</span>
                                    </div>
                                    <span className={`fs-0 fw-bolder ${isDark ? 'text-white' : 'text-800'}`}>{tot_received}</span>
                                </div>

                                <div className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#d1fae5', color: isDark ? '#34d399' : '#059669' }}>
                                            <FontAwesomeIcon icon="check-circle" className="fs--1" />
                                        </div>
                                        <span className={`fs-9 fw-bold ${isDark ? 'text-400' : 'text-600'}`}>Not Responded</span>
                                    </div>
                                    <span className={`fs-0 fw-bolder ${isDark ? 'text-white' : 'text-800'}`}>{tot_notresponded}</span>
                                </div>

                                <div className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#fef3c7', color: isDark ? '#fbbf24' : '#d97706' }}>
                                            <FontAwesomeIcon icon="clock" className="fs--1" />
                                        </div>
                                        <span className={`fs-9 fw-bold ${isDark ? 'text-400' : 'text-600'}`}>Avg. Res. Time</span>
                                    </div>
                                    <span className={`fs-0 fw-bolder ${isDark ? 'text-white' : 'text-800'}`}>{avg_respmins}m</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {tot_notresponded > 0 && (
                        <div className="mt-4 p-2 rounded-3 border d-flex align-items-center gap-2 badge-subtle-danger border-subtle-danger text-danger">
                            <FontAwesomeIcon icon="exclamation-circle" className="fs--1" />
                            <span className="fs-10 fw-semibold text-capitalize">
                                Attention: <span className="fw-bolder">{tot_notresponded}</span> requests require immediate response.
                            </span>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

export default CommunicationTracker;